package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChevronRightIcon
import dev.lingvo.site.ds.LDangerIcon
import dev.lingvo.site.ds.LDisclosure
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LListChecksIcon
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LSkeletonShape
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One check's findings, behind the design system's own disclosure.
 *
 * The header used to be a hand-rolled `<button>` with a rotating chevron and no `aria-controls`, so
 * a screen reader was told the control expanded something without being told what. [LDisclosure]
 * owns that contract now — the panel element stays in the DOM carrying `hidden`, so the reference
 * never dangles — and this file keeps only what is specific to a QA group: the glyph, the counts,
 * the whole-check exemption, and the sentence admitting the list is capped.
 */
@Composable
internal fun QaIssueGroup(
    group: QaGroup,
    collapsed: Boolean,
    onToggle: () -> Unit,
    onOpenIssue: (QaIssueDto) -> Unit,
    /** Null for anyone who may not write exemptions, which is everyone below admin. */
    onExempt: ((QaIssueDto) -> Unit)?,
    /**
     * Exempt this whole check at once. Null when [onExempt] is, and for a group of one.
     *
     * The count it acts on is every finding this check has under the current filter — including the
     * ones the page is not drawing. That is the point: the reader is answering "this check is wrong
     * for us", and an action that quietly stopped at the rendered rows would leave the rest blocking
     * a release with nothing saying so.
     */
    onExemptAll: (() -> Unit)? = null,
) {
    val hidden = (group.total - group.issues.size).coerceAtLeast(0)
    LDisclosure(
        title = group.check.displayName(),
        expanded = !collapsed,
        onExpandedChange = { onToggle() },
        modifier = Modifier.fillMaxWidth(),
        badge = {
            Div(QaSummaryStyle.toModifier().toAttrs()) {
                if (group.errors > 0) {
                    LBadge(
                        tPlural("editor.qa_issue_groups.count.error", group.errors, "{count} error", "{count} errors"),
                        variant = LBadgeVariant.DANGER,
                        dot = true,
                    )
                }
                if (group.warnings > 0) {
                    LBadge(
                        tPlural(
                            "editor.qa_issue_groups.count.warning",
                            group.warnings,
                            "{count} warning",
                            "{count} warnings",
                        ),
                        variant = LBadgeVariant.WARNING,
                        dot = true,
                    )
                }
            }
        },
        icon = group.check.icon(),
    ) {
        // Below the header rather than in it: the header is itself a button, and the same rule that
        // puts "Exempt" beside a row rather than inside it applies here.
        onExemptAll?.let { exemptAll ->
            Div(QaGroupBulkActionStyle.toModifier().toAttrs()) {
                LButton(
                    tPlural(
                        "editor.qa_issue_groups.exempt_all",
                        group.total,
                        "Exempt {count} string",
                        "Exempt all {count} strings",
                    ),
                    exemptAll,
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LListChecksIcon() },
                )
            }
        }
        group.issues.forEach { issue ->
            // **The exempt control the settings page has always pointed at.** Its empty state says
            // exemptions "are added from the QA list, where you can see the string that tripped the
            // check" — and no screen in the app could create one.
            //
            // Beside the row, not inside it: `QaIssueRow` is itself a Button, and nesting one
            // interactive element in another is neither clickable nor announceable.
            Div(QaRowWithActionStyle.toModifier().toAttrs()) {
                QaIssueRow(issue = issue, onOpen = { onOpenIssue(issue) })
                onExempt?.let { exempt ->
                    LButton(
                        t("editor.qa_issue_groups.exempt", "Exempt"),
                        { exempt(issue) },
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        // The visible word is right — the row beside it supplies the context a
                        // sighted reader needs — but a group renders up to 200 of these, and a
                        // screen reader's button list was 200 identical "Exempt" entries with
                        // nothing to choose between them.
                        accessibleLabel = trf(
                            "editor.qa_issue_groups.exempt_key_in_locale",
                            "Exempt {key} in {locale}",
                            "key" to issue.keyName,
                            "locale" to issue.locale,
                        ),
                    )
                }
            }
        }
        // Said, rather than silently truncated: a list that stops short and does not mention it
        // reads as "that is all of them".
        if (hidden > 0) {
            Div(QaGroupOverflowStyle.toModifier().toAttrs()) {
                Text(
                    tPlural(
                        "editor.qa_issue_groups.more_not_shown",
                        hidden,
                        "{count} more of this check is not shown. Filter to one language, or fix " +
                            "these first — the rest are the same problem.",
                        "{count} more of this check are not shown. Filter to one language, or fix " +
                            "these first — the rest are the same problem.",
                    ),
                )
            }
        }
    }
}

@Composable
private fun QaIssueRow(
    issue: QaIssueDto,
    onOpen: () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val tone = if (issue.severity == QaSeverity.ERROR) palette.danger else palette.warning
    val severityLabel = issue.severity.severityWord()
    Button(
        QaIssueRowStyle.toModifier().toAttrs {
            type(ButtonType.Button)
            attr(
                "aria-label",
                trf(
                    "editor.qa_issue_groups.issue_open_in_editor",
                    "{severity}: {key}. {message}. Open in editor",
                    "severity" to severityLabel,
                    "key" to issue.keyName,
                    "message" to issue.message,
                ),
            )
            onClick { onOpen() }
        },
    ) {
        // **Severity in shape as well as hue.** What sat here was a 9px circle whose only
        // difference between an error and a warning was `palette.danger` against `palette.warning`,
        // and the severity *word* lives in `QaMobileMetaStyle`, which is `display: none` from
        // `Breakpoint.MD` up. So on a desktop viewport a reader with low colour vision had nothing
        // left to separate a blocking error from a warning they may ship past. Same two glyphs
        // `ReviewCard` already draws for the same two severities; the word is still what assistive
        // technology hears, in the row's `aria-label` above.
        Span(
            Modifier
                .color(tone)
                .iconSize(IconSize.SM)
                .toAttrs { attr("aria-hidden", "true") },
        ) {
            if (issue.severity == QaSeverity.ERROR) LDangerIcon() else LWarningIcon()
        }
        Span(QaKeyStyle.toModifier().monoFont().toAttrs()) { Text(issue.keyName) }
        Div(QaMessageStyle.toModifier().toAttrs()) { Text(issue.message) }
        Div(QaDesktopCellStyle.toModifier().toAttrs()) {
            LBadge(
                text = "${localeFlagPrefix(issue.locale)}${issue.locale}",
                variant = LBadgeVariant.NEUTRAL,
            )
        }
        Div(QaDesktopCellStyle.toModifier().toAttrs()) {
            Span(QaOpenLabelStyle.toModifier().toAttrs()) {
                Text(t("editor.qa_issue_groups.open_in_editor", "Open in editor"))
                LChevronRightIcon(Modifier.iconSize(IconSize.MD))
            }
        }
        Div(QaMobileMetaStyle.toModifier().toAttrs()) {
            LBadge(
                text = severityLabel,
                variant = if (issue.severity == QaSeverity.ERROR) LBadgeVariant.DANGER else LBadgeVariant.WARNING,
            )
            LBadge(
                text = "${localeFlagPrefix(issue.locale)}${issue.locale}",
                variant = LBadgeVariant.NEUTRAL,
            )
            Span(QaOpenLabelStyle.toModifier().toAttrs()) {
                Text(t("editor.qa_issue_groups.open_in_editor", "Open in editor"))
                LChevronRightIcon(Modifier.iconSize(IconSize.MD))
            }
        }
    }
}

/**
 * No open QA issues. **Not the same claim as "ready to ship", and this page cannot make that one.**
 *
 * QA knows about placeholders, terminology and length. It knows nothing about strings awaiting
 * review, blocked strings, or machine output nobody has looked at — and the readiness gate weighs
 * all five. A project pre-translated entirely by machine has zero QA issues and zero shippable
 * locales; this state used to call it ready while the band on the project page correctly said "fr
 * has 400 unreviewed machine strings". Two green verdicts that disagree is worse than one, and the
 * wrong one was the one phrased as a decision.
 *
 * Nothing here refreshes either. The status line four lines above says so honestly; the description
 * used to promise checks would "appear automatically", which invited people to leave the tab open.
 */
@Composable
internal fun QaClearState(projectName: String, onOpenProject: (() -> Unit)? = null) {
    LEmptyState(
        title = t("editor.qa_issue_groups.no_open_qa_issues", "No open QA issues"),
        description = trf(
            "editor.qa_issue_groups.project_has_no_open_checks",
            "{project} has no open checks as of this page load. Release readiness — review state, " +
                "unanswered questions and unreviewed machine output — is on the project page.",
            "project" to projectName,
        ),
        modifier = Modifier.fillMaxWidth(),
        icon = LEmptyStateIcon.SUCCESS,
    ) {
        LBadge(t("editor.qa_issue_groups.checks_clean", "Checks clean"), variant = LBadgeVariant.SUCCESS, dot = true)
        onOpenProject?.let {
            LButton(t("editor.qa_issue_groups.see_release_readiness", "See release readiness"), it, variant = LButtonVariant.SECONDARY)
        }
    }
}

@Composable
internal fun QaFilteredEmptyState(
    errorsOnly: Boolean,
    locale: String,
    onClear: () -> Unit,
) {
    val description = when {
        errorsOnly && locale.isNotBlank() -> trf(
            "editor.qa_issue_groups.no_errors_in_locale",
            "{language} has no errors in the current checks.",
            "language" to localeInfo(locale).englishName,
        )
        errorsOnly -> tr(
            "editor.qa_issue_groups.warnings_but_no_errors",
            "There are warnings, but no errors in the current locale scope.",
        )
        locale.isNotBlank() -> trf(
            "editor.qa_issue_groups.no_issues_in_locale",
            "{language} has no issues in the current checks.",
            "language" to localeInfo(locale).englishName,
        )
        else -> tr("editor.qa_issue_groups.no_issues_match", "No issues match the current filters.")
    }
    LEmptyState(
        title = t("editor.qa_issue_groups.no_issues_match_these_filters", "No issues match these filters"),
        description = description,
        modifier = Modifier.fillMaxWidth(),
        icon = LEmptyStateIcon.SEARCH,
    ) {
        LButton(t("editor.qa_issue_groups.clear_filters", "Clear filters"), onClear, variant = LButtonVariant.SECONDARY)
    }
}

/**
 * The QA page while its issues load.
 *
 * **One live region, twenty-nine bars.** Every `LSkeleton` defaults to being its own `role="status"`
 * announcing "Loading content", and this state draws two plus three groups of nine — so a screen
 * reader heard the same sentence twenty-nine times before the page had said anything. That is the
 * failure `LSkeleton`'s own KDoc names and `LoadingState` already solved; this state predates the
 * fix and drew its own bars, so it never got it. The bars are decorative — the information is "this
 * is loading", and one sentence carries it.
 */
@Composable
internal fun QaLoadingState() {
    PageHeader(
        title = t("editor.qa_issue_groups.quality_assurance", "Quality assurance"),
        subtitle = t("editor.qa_issue_groups.find_and_fix_translation_risks_before", "Find and fix translation risks before they ship."),
    )
    Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
        Text(t("editor.qa_issue_groups.loading_quality_checks", "Loading quality checks"))
    }
    Div(QaFilterBarStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
        LSkeleton(width = 260.px, height = 40.px, label = null)
        LSkeleton(width = 250.px, height = 20.px, shape = LSkeletonShape.TEXT, label = null)
    }
    repeat(3) {
        Div(QaGroupStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
            Div(QaSkeletonRowStyle.toModifier().toAttrs()) {
                LSkeleton(width = 210.px, height = 20.px, shape = LSkeletonShape.TEXT, label = null)
                LSkeleton(width = 100.px, height = 20.px, shape = LSkeletonShape.TEXT, label = null)
                LSkeleton(width = 84.px, height = 20.px, shape = LSkeletonShape.TEXT, label = null)
            }
            repeat(2) {
                Div(QaSkeletonRowStyle.toModifier().toAttrs()) {
                    LSkeleton(width = 180.px, height = 16.px, shape = LSkeletonShape.TEXT, label = null)
                    LSkeleton(height = 16.px, shape = LSkeletonShape.TEXT, label = null)
                    LSkeleton(width = 72.px, height = 16.px, shape = LSkeletonShape.TEXT, label = null)
                }
            }
        }
    }
}

/**
 * A row and the one control that sits outside it.
 *
 * The row is a `Button` spanning the full width, so the exempt action cannot live inside it — this
 * puts them side by side and lets the row keep taking the remaining space.
 */
val QaRowWithActionStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            // Wraps. Without this the row — itself a Button carrying the surface a reader reads as
            // "the issue" — was squeezed to make room for a 110px ghost button: at 390 its ground
            // stopped at about three quarters of the column, its message wrapped to three lines to
            // free the space, and Exempt sat vertically centred on the bare page ground outside
            // any surface, which reads as a rendering fault rather than as a control.
            .flexWrap(FlexWrap.Wrap)
            // So a wrapped action lands under the row's right edge rather than at the far left of
            // its own line, where it would read as the next issue's first word.
            .justifyContent(JustifyContent.End)
            .gap(Space.XS)
            .fillMaxWidth()
    }
    // The row takes the line to itself until there is room for both; below that basis the action
    // drops beneath it instead of compressing it. On the row itself rather than in `QaStyles.kt`,
    // because it is this container's layout decision and `QaIssueRowStyle` is also used where
    // there is no action beside it.
    cssRule(" > :first-child") {
        Modifier.styleModifier {
            property("flex", "1 1 20rem")
            property("min-width", "0")
        }
    }
}

/** The line that admits a group was truncated. */
val QaGroupOverflowStyle = CssStyle.base {
    Modifier
        .padding(topBottom = Space.SM, leftRight = Space.MD)
        .fontSize(TypeScale.Small)
        .color(colorMode.toSitePalette().textSecondary)
}

/**
 * The row the whole-check action sits on.
 *
 * Right-aligned so it lands under the counts in the header rather than over the first issue's
 * label, and separated by a hairline so it reads as acting on the group below it rather than as the
 * group's first row.
 */
val QaGroupBulkActionStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.End)
        .padding(topBottom = Space.XS, leftRight = Space.MD)
        .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
}
